import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, BarChart3, Store, Users, DollarSign, Package, 
  UserCheck, LayoutGrid, TrendingUp, Award, Star,
  Briefcase, Target, Smile, ShieldCheck, Flame,
  Layers, CalendarDays, History, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

// RENDERER PLACEHOLDER SUPER RINGAN (Mencegah Overload Jaringan)
const ImagePlaceholder = () => (
  <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center text-slate-400 gap-1 select-none animate-pulse">
    <ImageIcon className="w-4 h-4 opacity-60" />
    <span className="text-[9px] font-medium tracking-wider">NO IMAGE</span>
  </div>
);

export default function TopProductsCard({ selectedBrand, setSelectedBrand, kodeToko = "01447", bulanAktif = "ALL", data = [] }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // State untuk menyimpan profile customer yang diklik
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState(null);
  const [customerKpiData, setCustomerKpiData] = useState(null);
  const [customerKpiLoading, setCustomerKpiLoading] = useState(false);

  // State Utama untuk Modal Popup Analisis Detail Produk
  const [selectedProductAnalysis, setSelectedProductAnalysis] = useState(null);
  const [analysisData, setAnalysisData] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('toko'); // 'toko' atau 'customer'

  // State Tambahan untuk Modal Profile KPI Crew + Data Klaim Penjualan
  const [selectedCrewProfile, setSelectedCrewProfile] = useState(null);
  const [selectedCompareCrew, setSelectedCompareCrew] = useState(null);

  const allCrewList = data || [];
  const [crewClaimsData, setCrewClaimsData] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);

  // Fungsi utilitas mendeteksi broken image url secara lokal
  const [brokenImages, setBrokenImages] = useState({});

  const handleImageError = (kode_extend) => {
    setBrokenImages(prev => ({ ...prev, [kode_extend]: true }));
  };

  // 1. Fetch data utama top 10 untuk layout Card luar
  useEffect(() => {
    const fetchTopProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/sales/popular-products`, {
          params: {
            kode_toko: kodeToko,
            bulan: bulanAktif,
            limit: 10,
            sort_by: 'qty',
            brand: selectedBrand
          }
        });
        if (response.data?.success) {
          setProducts(response.data.data);
        }
      } catch (error) {
        console.error("Gagal memuat data top produk:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopProducts();
  }, [selectedBrand, kodeToko, bulanAktif]);

  // 2. Fetch data analisis (Toko & Customer) saat sebuah produk di-klik / dipilih
  useEffect(() => {
    if (!selectedProductAnalysis) return;

    const fetchProductAnalysis = async () => {
      setAnalysisLoading(true);
      try {
        const response = await axios.get(`/api/sales/products/analysis`, {
          params: {
            kode_extend: selectedProductAnalysis.kode_extend,
            bulan: bulanAktif
          }
        });
        if (response.data?.success) {
          setAnalysisData(response.data.data);
        }
      } catch (error) {
        console.error("Gagal memuat analisis detail produk:", error);
      } finally {
        setAnalysisLoading(false);
      }
    };

    fetchProductAnalysis();
  }, [selectedProductAnalysis, bulanAktif]);

  // 3. Fetch data klaim penjualan crew selama 1 bulan saat profil crew dibuka
  useEffect(() => {
    if (!selectedCrewProfile) {
      setCrewClaimsData([]);
      return;
    }

    const fetchCrewClaims = async () => {
      setClaimsLoading(true);
      try {
        const sekarang = new Date();
        const tahun = sekarang.getFullYear(); 
        const bulanFormat = String(sekarang.getMonth() + 1).padStart(2, '0'); 
        const tanggalDefault = `${tahun}-${bulanFormat}-01`; 

        const response = await axios.get(`/api/sales/crew-claims`, {
          params: {
            id_sunfish: selectedCrewProfile.id_sunfish,
            kode_toko: kodeToko,
            bulan: bulanAktif !== "ALL" ? bulanAktif : tanggalDefault 
          }
        });
        
        if (response.data?.success) {
          setCrewClaimsData(response.data.data);
        }
      } catch (error) {
        console.error("Gagal memuat data klaim penjualan crew:", error);
      } finally {
        setClaimsLoading(false);
      }
    };

    fetchCrewClaims();
  }, [selectedCrewProfile, kodeToko, bulanAktif]);

  // 4. Fetch data KPI Customer berdasarkan id_member dari database cart_header
  useEffect(() => {
    if (!selectedCustomerProfile || !selectedCustomerProfile.id_member) {
      setCustomerKpiData(null);
      return;
    }

    const fetchCustomerKpi = async () => {
      setCustomerKpiLoading(true);
      try {
        const response = await axios.get(`/api/sales/customer-kpi`, {
          params: {
            id_member: selectedCustomerProfile.id_member
          }
        });
        if (response.data?.success) {
          setCustomerKpiData(response.data.data); 
        }
      } catch (error) {
        console.error("Gagal memuat KPI Detail Customer:", error);
      } finally {
        setCustomerKpiLoading(false);
      }
    };

    fetchCustomerKpi();
  }, [selectedCustomerProfile]);

  // Komputasi Analisis Data Ringkas untuk Card Utama
  const totalQtyTop10 = products.reduce((acc, curr) => acc + parseInt(curr.total_qty || 0), 0);
  const totalRevenueTop10 = products.reduce((acc, curr) => acc + parseInt(curr.net_revenue || 0), 0);

  // Summary Metrics khusus di dalam Modal Popup
  const totalAnalysisQty = analysisData.reduce((acc, curr) => acc + parseInt(curr.qty || 0), 0);
  const totalAnalysisNetto = analysisData.reduce((acc, curr) => acc + parseInt(curr.netto || 0), 0);
  const totalUniqueStores = new Set(analysisData.map(item => item.nama_toko)).size;
  const totalUniqueCustomers = new Set(analysisData.map(item => item.id_member || item.nama_customer || "Guest")).size;

  // Agregasi Data SQL untuk Tab Analisis Toko + Crew Kontributor
  const getStoreAnalysis = () => {
    const stores = {};
    const crewIds = {}; 

    analysisData.forEach(item => {
      const storeName = item.nama_toko || "Toko Tidak Diketahui";
      const crewName = item.nama_crew || "No Name / System";
      
      if (item.nama_crew && item.id_sunfish) {
        crewIds[item.nama_crew] = item.id_sunfish;
      }
      
      if (!stores[storeName]) {
        stores[storeName] = { qty: 0, netto: 0, crews: {} };
      }
      stores[storeName].qty += parseInt(item.qty || 0);
      stores[storeName].netto += parseInt(item.netto || 0);
      
      if (!stores[storeName].crews[crewName]) {
        stores[storeName].crews[crewName] = 0;
      }
      stores[storeName].crews[crewName] += parseInt(item.qty || 0);
    });

    return Object.keys(stores).map(name => {
      const crewList = stores[name].crews;
      const topCrew = Object.keys(crewList).reduce((a, b) => crewList[a] > crewList[b] ? a : b, "No Crew");
      
      return {
        nama_toko: name,
        qty: stores[name].qty,
        netto: stores[name].netto,
        top_crew: topCrew,
        top_crew_qty: crewList[topCrew] || 0,
        id_sunfish: crewIds[topCrew] || null 
      };
    }).sort((a, b) => b.qty - a.qty);
  };

  // Agregasi Data SQL untuk Tab Analisis Customer (Member)
  const getCustomerAnalysis = () => {
    if (!analysisData || analysisData.length === 0) return [];

    const customerMap = {};

    analysisData.forEach((item) => {
      const idMember = item.id_member || item.kode_customer || "GUEST-ID";
      const namaCust = item.nama_customer || "Non-Member / Guest";
      const namaToko = item.nama_toko || "-";

      if (!customerMap[idMember]) {
        customerMap[idMember] = {
          id_member: idMember,
          nama_customer: namaCust,
          toko_terkait: namaToko,
          qty: 0,
          netto: 0
        };
      }

      customerMap[idMember].qty += Number(item.qty || 0);
      customerMap[idMember].netto += Number(item.netto || 0);
    });

    return Object.values(customerMap).sort((a, b) => b.netto - a.netto);
  };

  // Variasi animasi Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.03 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 350, damping: 25 } }
  };

  return (
    <>
      {/* CARD MAIN DASHBOARD PANEL */}
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-all duration-300 relative flex flex-col justify-between overflow-hidden w-full h-full max-h-[700px]"
      >
        {/* HEADER PANEL */}
        <div className="border-b border-slate-100 pb-3 shrink-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-bold text-slate-800 tracking-wide flex items-center gap-1.5">
              <Award className="w-4 h-4 text-indigo-500" />
              <span>{selectedBrand ? `${selectedBrand} Best Articles` : "Top 10 Products"}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border tracking-wide uppercase font-medium ${
                selectedBrand ? "bg-indigo-50 text-indigo-700 border-indigo-100" : "bg-emerald-50 text-emerald-700 border-emerald-100"
              }`}>
                {selectedBrand ? "Filtered" : "All"}
              </span>
            </h3>
            {selectedBrand && (
              <button 
                onClick={() => setSelectedBrand(null)}
                className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium transition"
              >
                Clear
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 truncate mb-3">
            Klik pada salah satu produk untuk membuka deep-dive dashboard analisis distribusi.
          </p>

          {/* SEKSI ANALISIS DATA RINGKAS */}
          {!loading && products.length > 0 && (
            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-slate-600">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center gap-1">
                  <BarChart3 className="w-3 h-3 text-indigo-500" /> Volume Top 10
                </span>
                <span className="text-xs font-bold text-slate-700 mt-0.5">{totalQtyTop10.toLocaleString('id-ID')} pcs</span>
              </div>
              <div className="flex flex-col border-l border-slate-200 pl-2.5">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Total Omset Net</span>
                <span className="text-xs font-bold text-emerald-600 mt-0.5">Rp {totalRevenueTop10.toLocaleString('id-ID')}</span>
              </div>
            </div>
          )}
        </div>

        {/* LIST ITEM KONTEN TOP 10 */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate={loading ? "hidden" : "show"}
          className="flex flex-col gap-2 my-2 flex-1 overflow-y-auto pr-1 max-h-[440px] scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-2">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <div className="text-center text-xs text-slate-400 animate-pulse">Menghubungkan database...</div>
            </div>
          ) : products.length > 0 ? (
            products.map((product, index) => (
              <motion.div 
                variants={itemVariants}
                key={product.kode_extend} 
                onClick={() => { setSelectedProductAnalysis(product); setActiveTab('toko'); }}
                className="flex items-center justify-between gap-3 p-1.5 rounded-xl border border-transparent hover:border-indigo-100 hover:bg-indigo-50/40 cursor-pointer transition-all duration-150 group"
              >
                <div className="flex items-center gap-3 truncate">
                  <div className="flex items-center justify-center shrink-0 w-6">
                    <span className={`text-xs font-bold ${
                      index === 0 ? "text-indigo-600 text-sm" : index === 1 ? "text-amber-500" : index === 2 ? "text-emerald-500" : "text-slate-300"
                    }`}>
                      {index + 1 < 10 ? `0${index + 1}` : index + 1}
                    </span>
                  </div>
                  
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-slate-200/60 shadow-xs relative flex items-center justify-center">
                    {!product.gambar || brokenImages[product.kode_extend] ? (
                      <ImagePlaceholder />
                    ) : (
                      <img 
                        src={product.gambar} 
                        alt={product.kode_extend} 
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300 ease-out"
                        onError={() => handleImageError(product.kode_extend)}
                      />
                    )}
                  </div>
                  
                  <div className="truncate">
                    <h4 className="text-xs font-bold text-slate-800 truncate group-hover:text-indigo-600 transition">
                      {product.kode_extend}
                    </h4>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mt-0.5 font-medium">
                      {product.brand || "Umum"}
                    </p>
                    <div className="mt-1 flex items-center gap-1 text-slate-500 select-none">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
                      <span className="text-xs font-bold text-slate-700">
                        {product.rating ? Number(product.rating).toFixed(1) : "5.0"}
                      </span>
                      {product.total_reviews && (
                        <span className="text-[11px] text-slate-400 font-normal">
                          ({product.total_reviews})
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right shrink-0 flex flex-col items-end gap-1">
                  <span className="text-xs font-bold text-slate-700">
                    {parseInt(product.total_qty).toLocaleString('id-ID')} <span className="text-slate-400 font-normal">pcs</span>
                  </span>
                  <span className="text-[11px] font-semibold text-emerald-600">
                    Rp {parseInt(product.net_revenue || 0).toLocaleString('id-ID')}
                  </span>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center text-slate-400 text-xs py-20 italic">Tidak ada transaksi.</div>
          )}
        </motion.div>

        {/* TOMBOL LIHAT PRODUK PERTAMA */}
        <div className="mt-auto pt-2 border-t border-slate-100 shrink-0">
          <button 
            disabled={products.length === 0}
            onClick={() => { if(products[0]) { setSelectedProductAnalysis(products[0]); setActiveTab('toko'); } }}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2 px-4 text-xs font-bold transition-all duration-200 flex items-center justify-center gap-1.5 shadow-xs group disabled:opacity-50"
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Analisis Produk Terlaris (#1)</span>
          </button>
        </div>
      </motion.div>

      {/* MODAL POPUP EXECUTIVE DASHBOARD DEEP-DIVE */}
      <AnimatePresence>
        {selectedProductAnalysis && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProductAnalysis(null)}
              className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-50 flex items-center justify-center"
            />

            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 20 }}
                transition={{ type: "spring", duration: 0.4 }}
                className="pointer-events-auto bg-white text-slate-800 rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl flex flex-col overflow-hidden max-h-[90vh]"
              >
                {/* Header Popup Modal */}
                <div className="p-4 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl overflow-hidden border border-slate-200 bg-white shrink-0 shadow-sm flex items-center justify-center relative">
                      {!selectedProductAnalysis.gambar || brokenImages[`modal-${selectedProductAnalysis.kode_extend}`] ? (
                        <ImagePlaceholder />
                      ) : (
                        <img 
                          src={selectedProductAnalysis.gambar} 
                          alt="" 
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(`modal-${selectedProductAnalysis.kode_extend}`)}
                        />
                      )}
                    </div>
                    <div className="truncate">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs uppercase tracking-widest font-bold text-indigo-600">
                          {selectedProductAnalysis.brand || 'Product Brand'}
                        </span>
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono border border-slate-200/60">
                          {selectedProductAnalysis.kode_extend}
                        </span>
                        <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Layers className="w-3 h-3" /> Current Stock: {selectedProductAnalysis.stok_sekarang || 0} pcs
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-slate-900 tracking-tight mt-0.5 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        <span>Product Contribution Deep-Dive</span>
                      </h3>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedProductAnalysis(null)}
                    className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 border border-slate-200/40 transition shadow-xs"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* KPI TOP MATRIX HIGHLIGHTS */}
                <div className="grid grid-cols-4 gap-2.5 p-4 bg-slate-50/40 border-b border-slate-100">
                  <div className="bg-white border border-slate-200/80 p-2.5 rounded-xl flex items-center gap-3 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100/50">
                      <Store className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Sebaran Toko</p>
                      <h4 className="text-sm font-bold text-slate-700 mt-0.5">{analysisLoading ? "..." : `${totalUniqueStores} Outlet`}</h4>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200/80 p-2.5 rounded-xl flex items-center gap-3 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100/50">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Total Customer</p>
                      <h4 className="text-sm font-bold text-slate-700 mt-0.5">{analysisLoading ? "..." : `${totalUniqueCustomers} Pax`}</h4>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200/80 p-2.5 rounded-xl flex items-center gap-3 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100/50">
                      <Package className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Volume Transaksi</p>
                      <h4 className="text-sm font-bold text-slate-700 mt-0.5">{analysisLoading ? "..." : `${totalAnalysisQty.toLocaleString('id-ID')} pcs`}</h4>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200/80 p-2.5 rounded-xl flex items-center gap-3 shadow-xs">
                    <div className="w-8 h-8 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0 border border-cyan-100/50">
                      <DollarSign className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Net Turnover</p>
                      <h4 className="text-sm font-bold text-emerald-600 mt-0.5">{analysisLoading ? "..." : `Rp ${totalAnalysisNetto.toLocaleString('id-ID')}`}</h4>
                    </div>
                  </div>
                </div>

                {/* NAVIGASI TAB MODAL */}
                <div className="flex bg-slate-50/50 p-2 border-b border-slate-100 gap-2 shrink-0">
                  <button
                    onClick={() => setActiveTab('toko')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-xl border transition-all duration-200 ${
                      activeTab === 'toko' ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm' : 'text-slate-500 hover:text-slate-700 bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                    }`}
                  >
                    <Store className="w-4 h-4" />
                    <span>Distribusi Toko & Performa Crew</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('customer')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-xl border transition-all duration-200 ${
                      activeTab === 'customer' ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm' : 'text-slate-500 hover:text-slate-700 bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    <span>Customer & Demografi</span>
                  </button>
                </div>

                {/* CONTAINER DATA TAB LIST */}
                <div className="p-4 overflow-y-auto flex-1 bg-white min-h-[300px] scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                  {analysisLoading ? (
                    <div className="flex flex-col items-center justify-center py-28 gap-3">
                      <div className="w-7 h-7 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-xs text-slate-400 animate-pulse tracking-wide">Mengkalkulasi kontribusi tabel relasional...</p>
                    </div>
                  ) : analysisData.length === 0 ? (
                    <div className="text-center py-24 text-xs text-slate-400 italic">Data mutasi penjualan produk tidak ditemukan.</div>
                  ) : activeTab === 'toko' ? (
                    /* TAB UTAMA: TAB TOKO */
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-12 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 pb-1">
                        <span className="col-span-3">Nama Toko / Mitra</span>
                        <span className="col-span-2 text-center">Trend Kontribusi</span>
                        <span className="col-span-4">Crew Teraktif (Kontribusi)</span>
                        <span className="col-span-1 text-center">Qty</span>
                        <span className="col-span-2 text-right">Netto</span>
                      </div>

                      {(() => {
                        const storeData = getStoreAnalysis();
                        const maxQty = Math.max(...storeData.map(s => s.qty || 1), 1);

                        return storeData.map((store, i) => (
                          <div key={i} className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3 grid grid-cols-12 items-center hover:bg-indigo-50/30 hover:border-indigo-200 transition-all duration-150 group">
                            <div className="col-span-3 flex items-center gap-2.5 truncate pr-2">
                              <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 border border-indigo-100/50">
                                <Store className="w-3.5 h-3.5" />
                              </div>
                              <span className="text-xs font-bold text-slate-700 truncate group-hover:text-indigo-950">{store.nama_toko}</span>
                            </div>

                            <div className="col-span-2 px-3 flex items-center h-full">
                              <div className="w-full bg-slate-200/70 h-2 rounded-full overflow-hidden relative" title={`${((store.qty / maxQty) * 100).toFixed(0)}% dari volume puncak`}>
                                <div 
                                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-full rounded-full transition-all duration-500 ease-out"
                                  style={{ width: `${(store.qty / maxQty) * 100}%` }}
                                />
                              </div>
                            </div>

                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCrewProfile({ nama: store.top_crew, toko: store.nama_toko, id_sunfish: store.id_sunfish, qty_kontribusi: store.top_crew_qty });
                              }}
                              className="col-span-4 flex items-center gap-1.5 truncate text-xs text-slate-500 pr-2 cursor-pointer hover:bg-white p-1 rounded-lg border border-transparent hover:border-slate-200 hover:shadow-xs transition"
                            >
                              <Briefcase className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              <div className="truncate flex flex-col">
                                <span className="font-semibold text-slate-700 truncate group-hover:text-indigo-600 underline decoration-dotted">{store.top_crew}</span>
                                <span className="text-[10px] text-indigo-600 font-medium">Top contributor ({store.top_crew_qty} pcs)</span>
                              </div>
                            </div>

                            <div className="col-span-1 text-center">
                              <span className="text-xs font-bold text-slate-600 bg-white px-2 py-0.5 rounded-md border border-slate-200 shadow-2xs">
                                {store.qty.toLocaleString('id-ID')}
                              </span>
                            </div>

                            <div className="col-span-2 text-right">
                              <span className="text-xs font-bold text-emerald-600">
                                Rp {store.netto.toLocaleString('id-ID')}
                              </span>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    /* TAB KEDUA: TAB CUSTOMER DENGAN POPUP KPI */
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-12 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 pb-1">
                        <span className="col-span-5">Identitas Member / Customer</span>
                        <span className="col-span-4">Cluster Penyebaran Toko</span>
                        <span className="col-span-1 text-center">Qty</span>
                        <span className="col-span-2 text-right">Total Netto</span>
                      </div>

                      {getCustomerAnalysis().map((customer, i) => (
                        <div 
                          key={i} 
                          onClick={() => {
                            setSelectedCustomerProfile({
                              id_member: customer.id_member || "GUEST-ID",
                              nama: customer.nama_customer,
                              toko_terkait: customer.toko_terkait
                            });
                          }}
                          className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3 grid grid-cols-12 items-center hover:bg-indigo-50/30 hover:border-indigo-200 transition-all duration-150 group cursor-pointer select-none"
                        >
                          <div className="col-span-5 flex items-center gap-2.5 truncate pr-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border ${
                              customer.nama_customer.includes("Guest") || customer.nama_customer.includes("Non-Member")
                                ? 'bg-slate-100 text-slate-400 border-slate-200'
                                : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                            }`}>
                              <UserCheck className="w-3.5 h-3.5" />
                            </div>
                            <div className="truncate flex flex-col">
                              <span className="text-xs font-bold text-slate-700 truncate group-hover:text-indigo-600 underline decoration-dotted">
                                {customer.nama_customer}
                              </span>
                              {customer.netto > 500000 && (
                                <span className="text-[9px] text-amber-600 font-bold uppercase tracking-wider mt-0.5">
                                  ⚡ Top Spender
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="col-span-4 flex items-center gap-1 text-[11px] text-slate-500 pr-3 truncate">
                            <Target className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate italic font-medium text-slate-500">{customer.toko_terkait || "-"}</span>
                          </div>

                          <div className="col-span-1 text-center">
                            <span className="text-xs font-semibold text-slate-600">
                              {customer.qty.toLocaleString('id-ID')}
                            </span>
                          </div>

                          <div className="col-span-2 text-right">
                            <span className="text-xs font-bold text-indigo-600">
                              Rp {customer.netto.toLocaleString('id-ID')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL POPUP SUB-DASHBOARD: KPI CUSTOMER (CART_HEADER) */}
      <AnimatePresence>
        {selectedCustomerProfile && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCustomerProfile(null)}
              className="fixed inset-0 bg-slate-950/50 backdrop-blur-md z-[60] flex items-center justify-center"
            />

            <div className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                className="pointer-events-auto bg-white text-slate-800 rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md flex flex-col overflow-hidden"
              >
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
                      <UserCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 leading-tight">{selectedCustomerProfile.nama}</h3>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {selectedCustomerProfile.id_member}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedCustomerProfile(null)}
                    className="p-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-400 hover:text-slate-600 border border-slate-200 transition"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Content KPI */}
                <div className="p-4 bg-white flex flex-col gap-3">
                  <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Historical KPI (Cart Header Summary)</h4>
                  
                  {customerKpiLoading ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2">
                      <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-slate-400">Querying transaction history...</span>
                    </div>
                  ) : customerKpiData ? (
                    <div className="grid grid-cols-1 gap-2">
                      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <span className="text-xs font-semibold text-slate-500">Total Transaksi</span>
                        <span className="text-sm font-bold text-slate-800">
                          {parseInt(customerKpiData.total_transaksi || 0).toLocaleString('id-ID')} <span className="text-xs font-normal text-slate-400">kali</span>
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                        <span className="text-xs font-semibold text-slate-500">Total Produk Dibeli (Qty)</span>
                        <span className="text-sm font-bold text-slate-800">
                          {parseInt(customerKpiData.qty || 0).toLocaleString('id-ID')} <span className="text-xs font-normal text-slate-400">pcs</span>
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/40 border border-emerald-100/50">
                        <span className="text-xs font-semibold text-emerald-700">Total Nilai Bersih (Netto)</span>
                        <span className="text-sm font-bold text-emerald-600">
                          Rp {parseInt(customerKpiData.netto || 0).toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-xs text-slate-400 italic bg-slate-50 rounded-xl border border-dashed">
                      Gagal memuat rekam jejak atau member berstatus Guest.
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end">
                  <button
                    onClick={() => setSelectedCustomerProfile(null)}
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-xs font-semibold shadow-2xs transition"
                  >
                    Tutup Dashboard
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}